import React, { useCallback, useMemo, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { Column, Row, TableInstance } from 'react-table';
import { SearchResultsCountArgs } from 'hooks/useHypotheticalSearchResultsCount';
import { MapStatsUtil } from 'utils/map_stats_util';
import { newFilters } from 'utils/filters';

import { AlgoliaTrail } from '../../types/trails';
import { AlgoliaAlert } from '../../types/alerts';
import { getColumnFieldSort, getColumnFieldFuzzyFilter } from '../../utils/tables';
import Table, { COLUMN_ID_CHECKBOX, COLUMN_ID_ACTIONS } from '../Table';
import * as styles from '../Table/styles/styles.module.scss';
import TableColumn from '../Table/TableColumn';
import { RowData } from './types';
import AddTrailButton from './AddTrailButton';
import TrailNameColumn from './TrailNameColumn';
import TrailNameFilter from '../filters/TrailNameFilter';
import DifficultyColumn from './DifficultyColumn';
import RatingColumn from './RatingColumn';
import AlertsColumn from './AlertsColumn';
import ActionsColumn from './ActionsColumn';
import SelectedColumn from '../columns/SelectedColumn';
import SelectedTrailsColumnHeader from './SelectedTrailsColumnHeader';
import SelectedTrailsAlternateTableHeader from './SelectedTrailsAlternateTableHeader';
import { getSelectedTrailIds } from './utils';
import useFilters, { getTrailFilterString, getAlertFilterString } from '../../hooks/useFilters';
import { useSelector, useDispatch } from '../../redux/hooks';
import { getPendingTrailEdits } from '../../redux/slices/pendingTrailEdits/pendingTrailEdits';
import { selectEditing, setTrailIds } from '../../redux/slices/alertsDrawer/alertsDrawer';
import { fetchGroupsAreas } from '../../redux/slices/areas/areas';
import { selectStatus as selectTrailsStatus } from '../../redux/slices/trails/trails';
import { selectStatus as selectAlertsStatus } from '../../redux/slices/alerts/alerts';
import { fetchTrails } from '../../redux/thunks/filters';
import getFiltersConfigurations from './searchFilterConfiguration';

const columns: Column<RowData>[] = [
  {
    Header: SelectedTrailsColumnHeader,
    accessor: 'trailId',
    disableSortBy: true,
    Cell: SelectedColumn,
    width: 42,
    id: COLUMN_ID_CHECKBOX
  },
  {
    Header: 'Trail',
    accessor: 'trailName',
    filter: getColumnFieldFuzzyFilter('name'),
    sortType: getColumnFieldSort('name'),
    Cell: TrailNameColumn,
    Filter: TrailNameFilter,
    width: 225
  },
  {
    Header: 'Property',
    accessor: 'areaName',
    Cell: TableColumn,
    width: 200
  },
  {
    Header: 'Length',
    accessor: 'length',
    Cell: TableColumn,
    width: 80
  },
  {
    Header: 'Difficulty',
    accessor: 'difficulty',
    Cell: DifficultyColumn,
    width: 80
  },
  {
    Header: 'Rating',
    accessor: 'rating',
    sortType: getColumnFieldSort('rating'),
    Cell: RatingColumn,
    width: 80
  },
  {
    Header: 'Alerts',
    accessor: 'alerts',
    disableSortBy: true,
    Cell: AlertsColumn,
    width: 175
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    Cell: ActionsColumn,
    disableSortBy: true,
    width: 80,
    id: COLUMN_ID_ACTIONS
  }
];

const TrailsTable = (): JSX.Element => {
  const alertsDrawerEnabled = useSelector(state => state.enabledFeatures.alertsDrawer);
  const {
    currentUser,
    adminUser,
    groups,
    displayMetric,
    languageRegionCode,
    primaryAlgoliaIndexId,
    alertsAlgoliaIndexId,
    mobileBrowser,
    proUser,
    tabletBrowser
  } = useSelector(
    ({ authState }) => ({
      currentUser: authState.currentUser,
      adminUser: authState.adminUser,
      groups: authState.groups,
      displayMetric: authState.displayMetric,
      languageRegionCode: authState.languageRegionCode,
      primaryAlgoliaIndexId: authState.primaryAlgoliaIndexId,
      alertsAlgoliaIndexId: authState.alertsAlgoliaIndexId,
      mobileBrowser: authState.mobileBrowser,
      proUser: authState.proUser,
      tabletBrowser: authState.tabletBrowser
    }),
    shallowEqual
  );
  const editingAlert = useSelector(selectEditing);
  const trailIds = useSelector(state => state.pendingTrailEdits.trailIds);
  const { filters, defaultTrailFilters, trailFilters, alertFilters } = useFilters(
    newFilters({
      context: {
        adminUser,
        currentUser,
        displayMetric,
        languageRegionCode,
        mobileBrowser,
        primaryAlgoliaIndexId,
        proUser,
        tabletBrowser,
        featureFlags: {}
      }
    })
  );
  const { trails, started: trailsStarted, loading: trailsLoading } = useSelector(selectTrailsStatus, shallowEqual);
  const { alerts, started: alertsStarted, loading: alertsLoading } = useSelector(selectAlertsStatus, shallowEqual);
  const started = alertsStarted && trailsStarted;
  const loading = alertsLoading || trailsLoading;
  const dispatch = useDispatch();

  // Load areas first and without any dependence on filters since they will only be used to update filters
  useEffect(() => {
    if (groups.length !== 0) {
      dispatch(fetchGroupsAreas({ indexId: primaryAlgoliaIndexId, groups }));
    }
  }, [primaryAlgoliaIndexId, groups, dispatch]);

  // Reload the actual table data anytime filters change
  useEffect(() => {
    if (groups.length !== 0) {
      dispatch(getPendingTrailEdits(currentUser.id));
      dispatch(fetchTrails({ primaryAlgoliaIndexId, alertsAlgoliaIndexId, groups, defaultTrailFilters, trailFilters, alertFilters }));
    }
  }, [primaryAlgoliaIndexId, alertsAlgoliaIndexId, groups, defaultTrailFilters, trailFilters, alertFilters, dispatch, currentUser]);

  const onSelectedRowsChange = useCallback(
    (selectedFlatRows: Array<Row>) => {
      dispatch(setTrailIds(getSelectedTrailIds(selectedFlatRows)));
    },
    [dispatch]
  );

  const getTrailRow = useCallback(
    (trail: AlgoliaTrail): RowData => {
      const { ID, name, length, difficulty_rating, avg_rating, num_reviews, area_name, area_slug, alert_ids } = trail;
      const hasPendingEdit = trailIds.includes(ID);
      const formattedLength = MapStatsUtil.metersToFormattedUserUnits(length, 1, displayMetric, languageRegionCode);
      const contributeUrl = `/contribute/route?trail_id=${ID}`;
      const suggestEditUrl = `/${trail.slug}/edit`;
      const alertUrl = `/admin/bulk_trail_edit/${area_slug}`;
      const editUrl = adminUser ? contributeUrl : suggestEditUrl;
      const trailAlerts: AlgoliaAlert[] = [];
      (alert_ids || []).forEach(alertId => {
        const alert = alerts[alertId];
        if (alert) {
          trailAlerts.push(alert);
        }
      });

      if (alertFilters && trailAlerts.length === 0) {
        return null;
      }

      return {
        trailId: trail.ID,
        trailName: { editUrl, name, hasPendingEdit },
        areaName: area_name,
        length: formattedLength,
        difficulty: parseInt(difficulty_rating),
        rating: {
          rating: avg_rating,
          timesReviewed: num_reviews
        },
        actions: { editUrl, alertUrl },
        alerts: trailAlerts
      };
    },
    [trailIds, displayMetric, languageRegionCode, adminUser, alerts, alertFilters]
  );

  const getAddButton = (): JSX.Element => alertsDrawerEnabled && <AddTrailButton />;

  const getTableHeader = (tableInstance: TableInstance, headerClass: string): JSX.Element => {
    const { getToggleAllPageRowsSelectedProps, toggleAllPageRowsSelected, rows, selectedFlatRows } = tableInstance;
    return selectedFlatRows.length > 0 || editingAlert ? (
      <SelectedTrailsAlternateTableHeader
        getToggleAllPageRowsSelectedProps={getToggleAllPageRowsSelectedProps}
        toggleAllPageRowsSelected={toggleAllPageRowsSelected}
        totalRowCount={rows.length}
        selectedFlatRows={selectedFlatRows}
        headerClass={headerClass}
      />
    ) : null;
  };

  const searchResultsCountFunction = useCallback(
    async ({ changes }: SearchResultsCountArgs) => {
      const draftFilters = {
        ...filters,
        ...changes
      };
      const result = await dispatch(
        fetchTrails({
          primaryAlgoliaIndexId,
          alertsAlgoliaIndexId,
          groups,
          defaultTrailFilters,
          trailFilters: getTrailFilterString(draftFilters),
          alertFilters: getAlertFilterString(draftFilters),
          draft: true
        })
      );
      return Object.keys(result.payload).length;
    },
    [dispatch, primaryAlgoliaIndexId, alertsAlgoliaIndexId, groups, filters, defaultTrailFilters]
  );

  const data: RowData[] = useMemo(
    () =>
      Object.values(trails).reduce((result, trail) => {
        const row = getTrailRow(trail);
        if (row) {
          result.push(row);
        }

        return result;
      }, []),
    [trails, getTrailRow]
  );
  const initialState = useMemo(
    () => ({
      sortBy: [{ id: 'trailName', desc: false }]
    }),
    []
  );

  return (
    <Table
      columns={columns}
      data={data}
      initialState={initialState}
      loading={!started || loading}
      autoResetFilters={false}
      resultsCount={Object.keys(trails).length}
      getAddButton={getAddButton}
      getTableHeader={getTableHeader}
      getRowProps={(row: Record<string, any>) => ({
        className: row.isSelected || editingAlert ? styles.selectedRow : null
      })}
      onSelectedRowsChange={onSelectedRowsChange}
      getFiltersConfigurations={getFiltersConfigurations}
      searchResultsCountFunction={searchResultsCountFunction}
    />
  );
};

export default TrailsTable;
